import React, { useState, useEffect } from 'react';
import { tickets as ticketsApi, projects as projectsApi } from '../services/api';
import { useProject } from '../context/ProjectContext';
import TicketCard from '../components/TicketCard';
import TicketModal from '../components/TicketModal';
import NewTicketModal from '../components/NewTicketModal';
import AlertModal from '../components/AlertModal';

const KanbanBoard = () => {
  const { selectedProject } = useProject();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketColumn, setNewTicketColumn] = useState('todo');
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [deployLoading, setDeployLoading] = useState(false);
  const [deployMessage, setDeployMessage] = useState(null);
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      loadTickets(selectedProject.id);
    }
  }, [selectedProject]);

  const loadTickets = async (projectId) => {
    try {
      const response = await ticketsApi.getAllForProject(projectId);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    }
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleTicketUpdate = async () => {
    if (selectedProject) {
      await loadTickets(selectedProject.id);
    }
    setSelectedTicket(null);
  };

  const handleNewTicket = (column) => {
    setNewTicketColumn(column);
    setShowNewTicketModal(true);
  };

  const handleTicketCreated = async () => {
    setShowNewTicketModal(false);
    if (selectedProject) {
      await loadTickets(selectedProject.id);
    }
  };

  const columns = [
    { id: 'todo', title: 'To Do', color: '#3b82f6' },
    { id: 'planning', title: 'Planning', color: '#8b5cf6' },
    { id: 'ready_to_implement', title: 'Ready to Implement', color: '#06b6d4' },
    { id: 'in_progress', title: 'In Progress', color: '#f59e0b' },
    { id: 'complete', title: 'Complete', color: '#10b981' }
  ];

  const getTicketsForColumn = (columnId) => {
    return tickets.filter(ticket => ticket.status === columnId);
  };

  const handleDragStart = (ticket) => {
    setDraggedTicket(ticket);
  };

  const handleDragEnd = () => {
    setDraggedTicket(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedTicket || draggedTicket.status === columnId) {
      setDraggedTicket(null);
      return;
    }

    try {
      await ticketsApi.updateStatus(draggedTicket.id, columnId);
      setDraggedTicket(null);
      if (selectedProject) {
        await loadTickets(selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to update ticket status:', error);
      setDraggedTicket(null);
    }
  };

  const handleDeployClick = () => {
    setShowDeployConfirm(true);
  };

  const handleDeployConfirm = async () => {
    if (!selectedProject) return;

    setShowDeployConfirm(false);
    setDeployLoading(true);
    setDeployMessage(null);

    try {
      const response = await projectsApi.deploy(selectedProject.id);
      setDeployMessage({ type: 'success', text: 'Deploy job submitted successfully! Check the Jobs page for status.' });
      setTimeout(() => setDeployMessage(null), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to deploy project';
      setDeployMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setDeployMessage(null), 5000);
    } finally {
      setDeployLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Kanban Board</h1>
          {selectedProject && (
            <p>{selectedProject.name} - {selectedProject.description}</p>
          )}
        </div>
        {selectedProject && selectedProject.deploy_script && (
          <button
            className="btn-deploy page-header-button"
            onClick={handleDeployClick}
            disabled={deployLoading}
          >
            {deployLoading ? 'Deploying...' : '🚀 Deploy Project'}
          </button>
        )}
      </div>

      {deployMessage && (
        <div className={`alert ${deployMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '20px' }}>
          {deployMessage.text}
        </div>
      )}

      {selectedProject ? (
        <div className="kanban-board">
          {columns.map(column => (
            <div key={column.id} className="kanban-column">
              <div className="kanban-column-header" style={{ borderTopColor: column.color }}>
                <h3>{column.title}</h3>
                <span className="ticket-count">{getTicketsForColumn(column.id).length}</span>
                <button
                  className="btn-add-ticket"
                  onClick={() => handleNewTicket(column.id)}
                  title="Add ticket"
                >
                  +
                </button>
              </div>
              <div
                className={`kanban-column-content ${dragOverColumn === column.id ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {getTicketsForColumn(column.id).map(ticket => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onClick={() => handleTicketClick(ticket)}
                    onDragStart={() => handleDragStart(ticket)}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedTicket?.id === ticket.id}
                  />
                ))}
                {getTicketsForColumn(column.id).length === 0 && (
                  <div className="empty-column">
                    <p>No tickets</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
            No project selected. Please select a project from the sidebar.
          </p>
        </div>
      )}

      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={handleTicketUpdate}
        />
      )}

      {showNewTicketModal && (
        <NewTicketModal
          projectId={selectedProject.id}
          initialStatus={newTicketColumn}
          onClose={() => setShowNewTicketModal(false)}
          onCreate={handleTicketCreated}
        />
      )}

      <AlertModal
        isOpen={showDeployConfirm}
        onClose={() => setShowDeployConfirm(false)}
        title="Deploy Project"
        message={`Are you sure you want to deploy "${selectedProject?.name}"? This will start the deployment process.`}
        type="confirm"
        confirmText="Deploy"
        onConfirm={handleDeployConfirm}
      />
    </div>
  );
};

export default KanbanBoard;
